export type NdjsonObject = Record<string, unknown>

export type NdjsonReadResult =
  | { success: true }
  | { success: false; reason: "empty_body" | "reader_error"; error?: unknown }

export function clampStreamPercent(value: unknown): number {
  return Math.max(0, Math.min(100, Number(value) || 0))
}

export function isTerminalStreamFailure(obj: NdjsonObject): boolean {
  return (
    obj.success === false ||
    (typeof obj.error === "string" &&
      obj.error.length > 0 &&
      obj.success !== true)
  )
}

export function isNdjsonLikeContentType(
  contentType: string,
  options: { allowGenericStream?: boolean } = {}
): boolean {
  const normalized = contentType.toLowerCase()
  return (
    normalized.includes("ndjson") ||
    (options.allowGenericStream === true && normalized.includes("stream"))
  )
}

export async function readNdjsonStream(
  response: Response,
  onObject: (obj: NdjsonObject) => void
): Promise<NdjsonReadResult> {
  const reader = response.body?.getReader()
  if (!reader) return { success: false, reason: "empty_body" }

  const decoder = new TextDecoder()
  let buffer = ""

  function processLine(line: string) {
    const trimmed = line.trim().replace(/^\uFEFF/, "")
    if (!trimmed) return

    let parsed: NdjsonObject
    try {
      parsed = JSON.parse(trimmed) as NdjsonObject
    } catch {
      // Preserve existing behavior: ignore malformed partial/diagnostic lines.
      return
    }

    onObject(parsed)
  }

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split("\n")
      buffer = lines.pop() ?? ""
      for (const line of lines) {
        processLine(line)
      }
    }

    buffer += decoder.decode()
    if (buffer.trim()) {
      processLine(buffer)
    }
  } catch (error) {
    return { success: false, reason: "reader_error", error }
  }

  return { success: true }
}
